// User Roles Hierarchy
export enum UserRole {
  SUPER_ADMIN = 'SUPER_ADMIN',
  DIRECTOR_GENERAL = 'DIRECTOR_GENERAL',
  BRANCH_MANAGER = 'BRANCH_MANAGER',
  PEDAGOGICAL_DIRECTOR = 'PEDAGOGICAL_DIRECTOR',
  FINANCIAL_MANAGER = 'FINANCIAL_MANAGER',
  MARKETING_MANAGER = 'MARKETING_MANAGER',
  COMMERCIAL_MANAGER = 'COMMERCIAL_MANAGER',
  CALL_CENTER_AGENT = 'CALL_CENTER_AGENT',
  PROGRAM_HEAD = 'PROGRAM_HEAD',
  ACADEMIC_COORDINATOR = 'ACADEMIC_COORDINATOR',
  TEACHER = 'TEACHER',
  STUDENT = 'STUDENT',
  PARENT = 'PARENT',
}

// Role display names in French
export const RoleDisplayNames: Record<UserRole, string> = {
  [UserRole.SUPER_ADMIN]: 'Super Administrateur',
  [UserRole.DIRECTOR_GENERAL]: 'Directeur Général',
  [UserRole.BRANCH_MANAGER]: 'Responsable de Site',
  [UserRole.PEDAGOGICAL_DIRECTOR]: 'Directeur Pédagogique',
  [UserRole.FINANCIAL_MANAGER]: 'Responsable Financier',
  [UserRole.MARKETING_MANAGER]: 'Responsable Marketing',
  [UserRole.COMMERCIAL_MANAGER]: 'Responsable Commercial',
  [UserRole.CALL_CENTER_AGENT]: 'Agent Call Center',
  [UserRole.PROGRAM_HEAD]: 'Chef de Filière',
  [UserRole.ACADEMIC_COORDINATOR]: 'Coordinateur Académique',
  [UserRole.TEACHER]: 'Professeur',
  [UserRole.STUDENT]: 'Étudiant',
  [UserRole.PARENT]: 'Parent / Tuteur',
};

// Role route prefixes
export const RoleRoutes: Record<UserRole, string> = {
  [UserRole.SUPER_ADMIN]: '/admin',
  [UserRole.DIRECTOR_GENERAL]: '/institution/:tenantId/dg',
  [UserRole.BRANCH_MANAGER]: '/institution/:tenantId/branch',
  [UserRole.PEDAGOGICAL_DIRECTOR]: '/institution/:tenantId/pedagogie',
  [UserRole.FINANCIAL_MANAGER]: '/institution/:tenantId/finance',
  [UserRole.MARKETING_MANAGER]: '/institution/:tenantId/marketing',
  [UserRole.COMMERCIAL_MANAGER]: '/institution/:tenantId/commercial',
  [UserRole.CALL_CENTER_AGENT]: '/institution/:tenantId/call-center',
  [UserRole.PROGRAM_HEAD]: '/institution/:tenantId/filiere',
  [UserRole.ACADEMIC_COORDINATOR]: '/institution/:tenantId/coordinator',
  [UserRole.TEACHER]: '/institution/:tenantId/teacher',
  [UserRole.STUDENT]: '/institution/:tenantId/student',
  [UserRole.PARENT]: '/institution/:tenantId/parent',
};

// Permissions - Synced with backend Prisma schema
export enum Permission {
  // Student Management
  STUDENT_CREATE = 'STUDENT_CREATE',
  STUDENT_BULK_CREATE = 'STUDENT_BULK_CREATE',
  STUDENT_VIEW = 'STUDENT_VIEW',
  STUDENT_VALIDATE_MODIFICATIONS = 'STUDENT_VALIDATE_MODIFICATIONS',
  STUDENT_EDIT_PROFILE = 'STUDENT_EDIT_PROFILE',
  STUDENT_DELETE = 'STUDENT_DELETE',
  STUDENT_ARCHIVE = 'STUDENT_ARCHIVE',
  STUDENT_SUSPEND_ACTIVATE = 'STUDENT_SUSPEND_ACTIVATE',
  STUDENT_VALIDATE_PROFILE = 'STUDENT_VALIDATE_PROFILE',
  STUDENT_RESET_PASSWORD = 'STUDENT_RESET_PASSWORD',
  STUDENT_RESEND_ACTIVATION = 'STUDENT_RESEND_ACTIVATION',

  // Staff Management - STAFF_VIEW gives full access to "Gestion du Personnel" menu
  STAFF_VIEW = 'STAFF_VIEW',
  STAFF_CREATE = 'STAFF_CREATE', // Includes creating teachers
  STAFF_BULK_CREATE = 'STAFF_BULK_CREATE',
  STAFF_BULK_CREATE_TEACHER = 'STAFF_BULK_CREATE_TEACHER',
  STAFF_VALIDATE_PROFILE = 'STAFF_VALIDATE_PROFILE',
  STAFF_VALIDATE_MODIFICATIONS = 'STAFF_VALIDATE_MODIFICATIONS',
  STAFF_EDIT_PROFILE = 'STAFF_EDIT_PROFILE',
  STAFF_DELETE = 'STAFF_DELETE',
  STAFF_ARCHIVE = 'STAFF_ARCHIVE',
  STAFF_SUSPEND_ACTIVATE = 'STAFF_SUSPEND_ACTIVATE',
  STAFF_RESET_PASSWORD = 'STAFF_RESET_PASSWORD',
  STAFF_RESEND_ACTIVATION = 'STAFF_RESEND_ACTIVATION',

  // Team Management
  TEAM_VIEW = 'TEAM_VIEW',

  // Reports Management (LEGACY - to be removed)
  REPORTS_VIEW = 'REPORTS_VIEW',
  REPORTS_GENERATE = 'REPORTS_GENERATE',
  REPORTS_EXPORT = 'REPORTS_EXPORT',
  REPORTS_EDEP_MANAGE = 'REPORTS_EDEP_MANAGE',

  // Tracking & Reports (Gestion du Suivi et Rapports)
  TRACKING_PROFILE_VIEW = 'TRACKING_PROFILE_VIEW',
  TRACKING_PROFILE_EXPORT = 'TRACKING_PROFILE_EXPORT',
  TRACKING_PHYSICAL_DOCS_VIEW = 'TRACKING_PHYSICAL_DOCS_VIEW',
  TRACKING_PHYSICAL_DOCS_EXPORT = 'TRACKING_PHYSICAL_DOCS_EXPORT',
  EDEP_VIEW = 'EDEP_VIEW',
  EDEP_GENERATE = 'EDEP_GENERATE',
  EDEP_EXPORT = 'EDEP_EXPORT',
  EDEP_HISTORY_VIEW = 'EDEP_HISTORY_VIEW',

  // Academic Management
  GRADES_VIEW = 'GRADES_VIEW',
  GRADES_EDIT = 'GRADES_EDIT',
  GRADES_VALIDATE = 'GRADES_VALIDATE',
  GRADES_MANAGE = 'GRADES_MANAGE',
  // Notes & Evaluation (separate from GRADES for granular menu control)
  NOTES_VIEW = 'NOTES_VIEW',
  NOTES_MANAGE = 'NOTES_MANAGE',
  ATTENDANCE_VIEW = 'ATTENDANCE_VIEW',
  ATTENDANCE_MANAGE = 'ATTENDANCE_MANAGE',
  ACADEMIC_CONFIG_VIEW = 'ACADEMIC_CONFIG_VIEW',
  ACADEMIC_CONFIG_MANAGE = 'ACADEMIC_CONFIG_MANAGE',

  // Financial - Legacy (general)
  PAYMENTS_VIEW = 'PAYMENTS_VIEW',
  PAYMENTS_MANAGE = 'PAYMENTS_MANAGE',

  // Finance Module - Tuition & Fees
  FINANCE_TUITION_VIEW = 'FINANCE_TUITION_VIEW',
  FINANCE_TUITION_MANAGE = 'FINANCE_TUITION_MANAGE',

  // Finance Module - Discounts
  FINANCE_DISCOUNT_VIEW = 'FINANCE_DISCOUNT_VIEW',
  FINANCE_DISCOUNT_MANAGE = 'FINANCE_DISCOUNT_MANAGE',
  FINANCE_DISCOUNT_APPROVE = 'FINANCE_DISCOUNT_APPROVE',
  FINANCE_DISCOUNT_DELETE = 'FINANCE_DISCOUNT_DELETE',

  // Finance Module - Payments
  FINANCE_PAYMENT_VIEW = 'FINANCE_PAYMENT_VIEW',
  FINANCE_PAYMENT_RECORD = 'FINANCE_PAYMENT_RECORD',
  FINANCE_PAYMENT_CANCEL = 'FINANCE_PAYMENT_CANCEL',

  // Finance Module - Bank Transfers
  FINANCE_TRANSFER_VIEW = 'FINANCE_TRANSFER_VIEW',
  FINANCE_TRANSFER_APPROVE = 'FINANCE_TRANSFER_APPROVE',

  // Finance Module - Receipts
  FINANCE_RECEIPT_GENERATE = 'FINANCE_RECEIPT_GENERATE',

  // Finance Module - Payment Schedules (Échéanciers)
  FINANCE_SCHEDULE_VIEW = 'FINANCE_SCHEDULE_VIEW',
  FINANCE_SCHEDULE_MANAGE = 'FINANCE_SCHEDULE_MANAGE',

  // Finance Module - Ancillary Fees (Frais Annexes)
  FINANCE_ANCILLARY_VIEW = 'FINANCE_ANCILLARY_VIEW',
  FINANCE_ANCILLARY_MANAGE = 'FINANCE_ANCILLARY_MANAGE',

  // Finance Module - Reminders & Suspensions
  FINANCE_REMINDER_VIEW = 'FINANCE_REMINDER_VIEW',
  FINANCE_REMINDER_MANAGE = 'FINANCE_REMINDER_MANAGE',
  FINANCE_SUSPENSION_VIEW = 'FINANCE_SUSPENSION_VIEW',
  FINANCE_SUSPENSION_MANAGE = 'FINANCE_SUSPENSION_MANAGE',

  // Payroll - Permanent Staff
  PAYROLL_PERMANENT_VIEW = 'PAYROLL_PERMANENT_VIEW',
  PAYROLL_PERMANENT_MANAGE = 'PAYROLL_PERMANENT_MANAGE',
  PAYROLL_PERMANENT_BANK_AUTH = 'PAYROLL_PERMANENT_BANK_AUTH',

  // Payroll - Vacataires
  PAYROLL_VACATAIRE_VIEW = 'PAYROLL_VACATAIRE_VIEW',
  PAYROLL_VACATAIRE_MANAGE = 'PAYROLL_VACATAIRE_MANAGE',
  PAYROLL_VACATAIRE_BANK_AUTH = 'PAYROLL_VACATAIRE_BANK_AUTH',

  // Payroll - Additional
  PAYROLL_OWN_VIEW = 'PAYROLL_OWN_VIEW',

  // Institution Bank
  INSTITUTION_BANK_VIEW = 'INSTITUTION_BANK_VIEW',
  INSTITUTION_BANK_MANAGE = 'INSTITUTION_BANK_MANAGE',

  // Finance Reporting
  FINANCE_REPORT_RECOVERY = 'FINANCE_REPORT_RECOVERY',
  FINANCE_REPORT_PAYROLL = 'FINANCE_REPORT_PAYROLL',
  FINANCE_REPORT_FORECAST = 'FINANCE_REPORT_FORECAST',
  FINANCE_REPORT_EXPORT = 'FINANCE_REPORT_EXPORT',

  // Settings (LEGACY - to be removed)
  SETTINGS_VIEW = 'SETTINGS_VIEW',
  SETTINGS_MANAGE = 'SETTINGS_MANAGE',

  // Profile Deadline Configuration (LEGACY - replaced by SETTINGS_USERS_*)
  PROFILE_DEADLINE_CONFIGURE = 'PROFILE_DEADLINE_CONFIGURE',
  PROFILE_AUTO_SUBMIT = 'PROFILE_AUTO_SUBMIT',

  // User Settings Permissions (Paramètres des Utilisateurs)
  SETTINGS_USERS_VIEW = 'SETTINGS_USERS_VIEW',
  SETTINGS_USERS_MANAGE = 'SETTINGS_USERS_MANAGE',

  // Permission Management
  PERMISSION_MANAGE = 'PERMISSION_MANAGE',

  // Gestion Pédagogique - Filières
  FILIERE_VIEW = 'FILIERE_VIEW',
  FILIERE_MANAGE = 'FILIERE_MANAGE',

  // Gestion Pédagogique - Groupes
  GROUP_VIEW = 'GROUP_VIEW',
  GROUP_MANAGE = 'GROUP_MANAGE',

  // Calendar & Planning Permissions
  CALENDAR_SETTINGS_VIEW = 'CALENDAR_SETTINGS_VIEW',
  CALENDAR_SETTINGS_MANAGE = 'CALENDAR_SETTINGS_MANAGE',
  CALENDAR_ANNUAL_VIEW = 'CALENDAR_ANNUAL_VIEW',
  CALENDAR_ANNUAL_INPUT = 'CALENDAR_ANNUAL_INPUT',       // @deprecated - use CALENDAR_ANNUAL_MANAGE
  CALENDAR_ACADEMIC_INPUT = 'CALENDAR_ACADEMIC_INPUT',   // @deprecated - use CALENDAR_ANNUAL_MANAGE
  CALENDAR_ANNUAL_MANAGE = 'CALENDAR_ANNUAL_MANAGE',     // Consolidates INPUT permissions
  SCHEDULE_VIEW = 'SCHEDULE_VIEW',
  SCHEDULE_MANAGE = 'SCHEDULE_MANAGE',
  SESSION_CANCEL = 'SESSION_CANCEL',
  EXAM_SCHEDULE_VIEW = 'EXAM_SCHEDULE_VIEW',             // View exam scheduling page
  EXAM_SCHEDULE_MANAGE = 'EXAM_SCHEDULE_MANAGE',         // Manage exam scheduling
  RATTRAPAGE_SCHEDULE_VIEW = 'RATTRAPAGE_SCHEDULE_VIEW', // View rattrapage scheduling page
  RATTRAPAGE_SCHEDULE_MANAGE = 'RATTRAPAGE_SCHEDULE_MANAGE', // Manage rattrapage scheduling
  ROOM_OCCUPANCY_VIEW = 'ROOM_OCCUPANCY_VIEW',           // Occupation des salles overlay (rooms by floor)

  // Jury de Délibération
  JURY_VIEW = 'JURY_VIEW',
  JURY_MANAGE = 'JURY_MANAGE',

  // Student Requests (Attestations)
  REQUEST_VIEW_OWN = 'REQUEST_VIEW_OWN',
  REQUEST_CREATE = 'REQUEST_CREATE',
  REQUEST_CANCEL_OWN = 'REQUEST_CANCEL_OWN',
  REQUEST_DOWNLOAD_OWN = 'REQUEST_DOWNLOAD_OWN',
  REQUEST_VIEW_ALL = 'REQUEST_VIEW_ALL',
  REQUEST_VALIDATE_FINANCE = 'REQUEST_VALIDATE_FINANCE',
  REQUEST_VALIDATE_ACADEMIC = 'REQUEST_VALIDATE_ACADEMIC',
  REQUEST_MANAGE = 'REQUEST_MANAGE',
  REQUEST_CONFIG = 'REQUEST_CONFIG',

  // Course Evaluation
  EVALUATION_VIEW = 'EVALUATION_VIEW',
  EVALUATION_MANAGE = 'EVALUATION_MANAGE',
  EVALUATION_EXPORT = 'EVALUATION_EXPORT',

  // Professor Monitoring (Qualité & Suivi)
  PROFESSOR_MONITORING_VIEW = 'PROFESSOR_MONITORING_VIEW',

  // Institution Management (Gestion Établissement)
  INSTITUTION_PROFILE_VIEW = 'INSTITUTION_PROFILE_VIEW',
  INSTITUTION_PROFILE_MANAGE = 'INSTITUTION_PROFILE_MANAGE',
  INFRASTRUCTURE_VIEW = 'INFRASTRUCTURE_VIEW',
  INFRASTRUCTURE_MANAGE = 'INFRASTRUCTURE_MANAGE',
  DEPARTMENTS_VIEW = 'DEPARTMENTS_VIEW',
  DEPARTMENTS_MANAGE = 'DEPARTMENTS_MANAGE',
  OFFICIAL_DOCUMENTS_VIEW = 'OFFICIAL_DOCUMENTS_VIEW',
  OFFICIAL_DOCUMENTS_MANAGE = 'OFFICIAL_DOCUMENTS_MANAGE',

}

// Permission display names in French
export const PermissionDisplayNames: Record<Permission, string> = {
  [Permission.STUDENT_CREATE]: 'Créer des étudiants',
  [Permission.STUDENT_BULK_CREATE]: 'Ajouter des étudiants en masse',
  [Permission.STUDENT_VIEW]: 'Voir les étudiants',
  [Permission.STUDENT_VALIDATE_MODIFICATIONS]: 'Valider les demandes de modifications',
  [Permission.STUDENT_EDIT_PROFILE]: 'Modifier le profil étudiant',
  [Permission.STUDENT_DELETE]: 'Supprimer les étudiants',
  [Permission.STUDENT_ARCHIVE]: 'Archiver / Désistement',
  [Permission.STUDENT_SUSPEND_ACTIVATE]: 'Suspendre / Activer les comptes',
  [Permission.STUDENT_VALIDATE_PROFILE]: 'Valider les profils',
  [Permission.STUDENT_RESET_PASSWORD]: 'Réinitialiser les mots de passe',
  [Permission.STUDENT_RESEND_ACTIVATION]: 'Renvoyer le lien d\'activation',
  [Permission.STAFF_VIEW]: 'Voir le personnel',
  [Permission.STAFF_CREATE]: 'Créer du personnel (inclut professeurs)',
  [Permission.STAFF_BULK_CREATE]: 'Créer du personnel en masse',
  [Permission.STAFF_BULK_CREATE_TEACHER]: 'Créer des professeurs en masse',
  [Permission.STAFF_VALIDATE_PROFILE]: 'Valider les profils du personnel',
  [Permission.STAFF_VALIDATE_MODIFICATIONS]: 'Valider les demandes de modifications',
  [Permission.STAFF_EDIT_PROFILE]: 'Modifier le profil du personnel',
  [Permission.STAFF_DELETE]: 'Supprimer le personnel',
  [Permission.STAFF_ARCHIVE]: 'Archiver / Démission',
  [Permission.STAFF_SUSPEND_ACTIVATE]: 'Suspendre / Activer le personnel',
  [Permission.STAFF_RESET_PASSWORD]: 'Réinitialiser les mots de passe',
  [Permission.STAFF_RESEND_ACTIVATION]: 'Renvoyer le lien d\'activation',
  [Permission.TEAM_VIEW]: 'Voir l\'équipe',
  [Permission.REPORTS_VIEW]: 'Voir les rapports',
  [Permission.REPORTS_GENERATE]: 'Générer des rapports',
  [Permission.REPORTS_EXPORT]: 'Exporter les rapports',
  [Permission.REPORTS_EDEP_MANAGE]: 'Gérer le suivi E-DEP',
  // Tracking & Reports
  [Permission.TRACKING_PROFILE_VIEW]: 'Voir le suivi des profils',
  [Permission.TRACKING_PROFILE_EXPORT]: 'Exporter le suivi des profils',
  [Permission.TRACKING_PHYSICAL_DOCS_VIEW]: 'Voir les documents physiques',
  [Permission.TRACKING_PHYSICAL_DOCS_EXPORT]: 'Exporter les documents physiques',
  [Permission.EDEP_VIEW]: 'Voir les documents E-DEP',
  [Permission.EDEP_GENERATE]: 'Générer les dossiers E-DEP',
  [Permission.EDEP_EXPORT]: 'Exporter les documents E-DEP',
  [Permission.EDEP_HISTORY_VIEW]: 'Voir l\'historique E-DEP',
  [Permission.GRADES_VIEW]: 'Voir les notes',
  [Permission.GRADES_EDIT]: 'Modifier les notes',
  [Permission.GRADES_VALIDATE]: 'Valider les notes',
  [Permission.GRADES_MANAGE]: 'Gérer les notes et absences',
  // Notes & Evaluation display names
  [Permission.NOTES_VIEW]: 'Consulter les notes et rattrapages',
  [Permission.NOTES_MANAGE]: 'Gérer/Exporter les notes et rattrapages',
  [Permission.ATTENDANCE_VIEW]: 'Consulter les absences',
  [Permission.ATTENDANCE_MANAGE]: 'Gérer/Exporter les absences',
  [Permission.ACADEMIC_CONFIG_VIEW]: 'Voir la configuration académique',
  [Permission.ACADEMIC_CONFIG_MANAGE]: 'Gérer la configuration académique',
  [Permission.PAYMENTS_VIEW]: 'Voir les paiements',
  [Permission.PAYMENTS_MANAGE]: 'Gérer les paiements',
  // Finance Module
  [Permission.FINANCE_TUITION_VIEW]: 'Voir la grille tarifaire',
  [Permission.FINANCE_TUITION_MANAGE]: 'Gérer la grille tarifaire',
  [Permission.FINANCE_DISCOUNT_VIEW]: 'Voir les remises',
  [Permission.FINANCE_DISCOUNT_MANAGE]: 'Gérer les remises',
  [Permission.FINANCE_DISCOUNT_APPROVE]: 'Approuver les remises',
  [Permission.FINANCE_DISCOUNT_DELETE]: 'Supprimer les remises',
  [Permission.FINANCE_PAYMENT_VIEW]: 'Voir les paiements étudiants',
  [Permission.FINANCE_PAYMENT_RECORD]: 'Enregistrer des paiements étudiants',
  [Permission.FINANCE_PAYMENT_CANCEL]: 'Supprimer des paiements étudiants',
  [Permission.FINANCE_TRANSFER_VIEW]: 'Voir les virements',
  [Permission.FINANCE_TRANSFER_APPROVE]: 'Approuver les virements',
  [Permission.FINANCE_RECEIPT_GENERATE]: 'Générer des reçus',
  [Permission.FINANCE_SCHEDULE_VIEW]: 'Voir les échéanciers types',
  [Permission.FINANCE_SCHEDULE_MANAGE]: 'Gérer les échéanciers types',
  [Permission.FINANCE_ANCILLARY_VIEW]: 'Voir les frais annexes',
  [Permission.FINANCE_ANCILLARY_MANAGE]: 'Gérer les frais annexes',
  [Permission.FINANCE_REMINDER_VIEW]: 'Voir les relances',
  [Permission.FINANCE_REMINDER_MANAGE]: 'Gérer les relances',
  [Permission.FINANCE_SUSPENSION_VIEW]: 'Voir les suspensions',
  [Permission.FINANCE_SUSPENSION_MANAGE]: 'Gérer les suspensions',
  [Permission.PAYROLL_PERMANENT_VIEW]: 'Voir la paie permanents',
  [Permission.PAYROLL_PERMANENT_MANAGE]: 'Gérer la paie permanents',
  [Permission.PAYROLL_PERMANENT_BANK_AUTH]: 'Générer les ordres de virement permanents',
  [Permission.PAYROLL_VACATAIRE_VIEW]: 'Voir la paie vacataires',
  [Permission.PAYROLL_VACATAIRE_MANAGE]: 'Gérer la paie vacataires',
  [Permission.PAYROLL_VACATAIRE_BANK_AUTH]: 'Générer les ordres de virement vacataires',
  [Permission.PAYROLL_OWN_VIEW]: 'Voir ses propres paiements',
  [Permission.INSTITUTION_BANK_VIEW]: 'Voir les informations bancaires',
  [Permission.INSTITUTION_BANK_MANAGE]: 'Gérer les informations bancaires',
  [Permission.FINANCE_REPORT_RECOVERY]: 'Voir les états de recouvrement',
  [Permission.FINANCE_REPORT_PAYROLL]: 'Voir les rapports de paie',
  [Permission.FINANCE_REPORT_FORECAST]: 'Voir les prévisions',
  [Permission.FINANCE_REPORT_EXPORT]: 'Exporter les données financières',
  [Permission.SETTINGS_VIEW]: 'Voir les paramètres',
  [Permission.SETTINGS_MANAGE]: 'Gérer les paramètres',
  [Permission.PROFILE_DEADLINE_CONFIGURE]: 'Configurer les délais de profil',
  [Permission.PROFILE_AUTO_SUBMIT]: 'Soumettre automatiquement les profils',
  // User Settings
  [Permission.SETTINGS_USERS_VIEW]: 'Voir les paramètres utilisateurs',
  [Permission.SETTINGS_USERS_MANAGE]: 'Gérer les paramètres utilisateurs',
  [Permission.PERMISSION_MANAGE]: 'Gérer les permissions',
  // Gestion Pédagogique
  [Permission.FILIERE_VIEW]: 'Voir les filières',
  [Permission.FILIERE_MANAGE]: 'Gérer les filières',
  [Permission.GROUP_VIEW]: 'Voir les groupes',
  [Permission.GROUP_MANAGE]: 'Gérer les groupes',
  // Calendar & Planning
  [Permission.CALENDAR_SETTINGS_VIEW]: 'Voir les paramètres du calendrier',
  [Permission.CALENDAR_SETTINGS_MANAGE]: 'Gérer les paramètres du calendrier',
  [Permission.CALENDAR_ANNUAL_VIEW]: 'Voir le planning annuel',
  [Permission.CALENDAR_ANNUAL_INPUT]: 'Saisir des événements généraux',
  [Permission.CALENDAR_ACADEMIC_INPUT]: 'Saisir des événements académiques',
  [Permission.CALENDAR_ANNUAL_MANAGE]: 'Gérer le planning annuel',
  [Permission.SCHEDULE_VIEW]: 'Voir les emplois du temps',
  [Permission.SCHEDULE_MANAGE]: 'Gérer les emplois du temps',
  [Permission.SESSION_CANCEL]: 'Annuler des séances',
  [Permission.EXAM_SCHEDULE_VIEW]: 'Voir le planning des examens',
  [Permission.EXAM_SCHEDULE_MANAGE]: 'Gérer le planning des examens',
  [Permission.RATTRAPAGE_SCHEDULE_VIEW]: 'Voir le planning des rattrapages',
  [Permission.RATTRAPAGE_SCHEDULE_MANAGE]: 'Gérer le planning des rattrapages',
  [Permission.ROOM_OCCUPANCY_VIEW]: "Voir l'occupation des salles",
  // Jury de Délibération
  [Permission.JURY_VIEW]: 'Voir les jurys de délibération',
  [Permission.JURY_MANAGE]: 'Gérer les jurys de délibération',
  // Student Requests (Attestations)
  [Permission.REQUEST_VIEW_OWN]: 'Voir ses propres demandes',
  [Permission.REQUEST_CREATE]: 'Créer des demandes',
  [Permission.REQUEST_CANCEL_OWN]: 'Annuler ses propres demandes',
  [Permission.REQUEST_DOWNLOAD_OWN]: 'Télécharger ses propres attestations',
  [Permission.REQUEST_VIEW_ALL]: 'Voir toutes les demandes',
  [Permission.REQUEST_VALIDATE_FINANCE]: 'Valider les demandes (Finance)',
  [Permission.REQUEST_VALIDATE_ACADEMIC]: 'Valider les demandes (Académique)',
  [Permission.REQUEST_MANAGE]: 'Gérer les demandes',
  [Permission.REQUEST_CONFIG]: 'Configurer les attestations',
  [Permission.EVALUATION_VIEW]: 'Voir évaluation des cours',
  [Permission.EVALUATION_MANAGE]: 'Gérer évaluation des cours',
  [Permission.EVALUATION_EXPORT]: 'Exporter évaluation des cours',
  [Permission.PROFESSOR_MONITORING_VIEW]: 'Voir suivi des professeurs',
  // Institution Management (Gestion Établissement)
  [Permission.INSTITUTION_PROFILE_VIEW]: 'Voir le profil de l\'établissement',
  [Permission.INSTITUTION_PROFILE_MANAGE]: 'Gérer le profil de l\'établissement',
  [Permission.INFRASTRUCTURE_VIEW]: 'Voir les bâtiments et salles',
  [Permission.INFRASTRUCTURE_MANAGE]: 'Gérer les bâtiments et salles',
  [Permission.DEPARTMENTS_VIEW]: 'Voir les départements',
  [Permission.DEPARTMENTS_MANAGE]: 'Gérer les départements',
  [Permission.OFFICIAL_DOCUMENTS_VIEW]: 'Voir les documents officiels',
  [Permission.OFFICIAL_DOCUMENTS_MANAGE]: 'Gérer les documents officiels',
};

// Permission categories for grouping in UI
export const PermissionCategories: Record<string, { label: string; permissions: Permission[] }> = {
  qualiteSuivi: {
    label: 'Qualité & Suivi',
    permissions: [
      Permission.PROFESSOR_MONITORING_VIEW,
      Permission.EVALUATION_VIEW,
      Permission.EVALUATION_MANAGE,
      Permission.EVALUATION_EXPORT,
    ],
  },
  institution: {
    label: 'Gestion Établissement',
    permissions: [
      Permission.INSTITUTION_PROFILE_VIEW,
      Permission.INSTITUTION_PROFILE_MANAGE,
      Permission.INFRASTRUCTURE_VIEW,
      Permission.INFRASTRUCTURE_MANAGE,
      Permission.DEPARTMENTS_VIEW,
      Permission.DEPARTMENTS_MANAGE,
      Permission.OFFICIAL_DOCUMENTS_VIEW,
      Permission.OFFICIAL_DOCUMENTS_MANAGE,
    ],
  },
  student: {
    label: 'Gestion des Étudiants',
    permissions: [
      Permission.STUDENT_VIEW,
      Permission.STUDENT_CREATE,
      Permission.STUDENT_BULK_CREATE,
      Permission.STUDENT_VALIDATE_MODIFICATIONS,
      Permission.STUDENT_EDIT_PROFILE,
      Permission.STUDENT_DELETE,
      Permission.STUDENT_ARCHIVE,
      Permission.STUDENT_SUSPEND_ACTIVATE,
      Permission.STUDENT_VALIDATE_PROFILE,
      Permission.STUDENT_RESET_PASSWORD,
      Permission.STUDENT_RESEND_ACTIVATION,
    ],
  },
  staff: {
    label: 'Gestion du Personnel',
    permissions: [
      Permission.STAFF_VIEW,
      Permission.STAFF_CREATE,
      Permission.STAFF_BULK_CREATE,
      Permission.STAFF_BULK_CREATE_TEACHER,
      Permission.STAFF_VALIDATE_PROFILE,
      Permission.STAFF_VALIDATE_MODIFICATIONS,
      Permission.STAFF_EDIT_PROFILE,
      Permission.STAFF_DELETE,
      Permission.STAFF_ARCHIVE,
      Permission.STAFF_SUSPEND_ACTIVATE,
      Permission.STAFF_RESET_PASSWORD,
      Permission.STAFF_RESEND_ACTIVATION,
      Permission.PERMISSION_MANAGE,
    ],
  },
  trackingReports: {
    label: 'Gestion du Suivi et Rapports',
    permissions: [
      Permission.TRACKING_PROFILE_VIEW,
      Permission.TRACKING_PROFILE_EXPORT,
      Permission.TRACKING_PHYSICAL_DOCS_VIEW,
      Permission.TRACKING_PHYSICAL_DOCS_EXPORT,
      Permission.EDEP_VIEW,
      Permission.EDEP_GENERATE,
      Permission.EDEP_EXPORT,
      Permission.EDEP_HISTORY_VIEW,
    ],
  },
  settings: {
    label: 'Paramètres',
    permissions: [
      Permission.SETTINGS_USERS_VIEW,
      Permission.SETTINGS_USERS_MANAGE,
      Permission.ACADEMIC_CONFIG_VIEW,
      Permission.ACADEMIC_CONFIG_MANAGE,
      Permission.CALENDAR_SETTINGS_VIEW,
      Permission.CALENDAR_SETTINGS_MANAGE,
    ],
  },
  // Note: GRADES_* permissions are for professors/teachers to edit grades directly (separate from menu access)
  // ACADEMIC_CONFIG_* permissions are already in 'settings' category
  notesEvaluation: {
    label: 'Notes & Évaluation',
    permissions: [
      Permission.NOTES_VIEW,
      Permission.NOTES_MANAGE,
      Permission.ATTENDANCE_VIEW,
      Permission.ATTENDANCE_MANAGE,
      Permission.JURY_VIEW,
      Permission.JURY_MANAGE,
    ],
  },
  financeModule: {
    label: 'Gestion Financière Étudiants',
    permissions: [
      // Frais & Relances - Grille Tarifaire
      Permission.FINANCE_TUITION_VIEW,
      Permission.FINANCE_TUITION_MANAGE,
      // Frais & Relances - Frais Annexes
      Permission.FINANCE_ANCILLARY_VIEW,
      Permission.FINANCE_ANCILLARY_MANAGE,
      // Frais & Relances - Échéanciers Types
      Permission.FINANCE_SCHEDULE_VIEW,
      Permission.FINANCE_SCHEDULE_MANAGE,
      // Frais & Relances - Remises & Bourses
      Permission.FINANCE_DISCOUNT_VIEW,
      Permission.FINANCE_DISCOUNT_MANAGE,
      Permission.FINANCE_DISCOUNT_APPROVE,
      Permission.FINANCE_DISCOUNT_DELETE,
      // Frais & Relances - Paramètres Relances
      Permission.FINANCE_REMINDER_VIEW,
      Permission.FINANCE_REMINDER_MANAGE,
      // Gestion des Paiements
      Permission.FINANCE_PAYMENT_VIEW,
      Permission.FINANCE_PAYMENT_RECORD,
      Permission.FINANCE_PAYMENT_CANCEL,
      // Virements Bancaires
      Permission.FINANCE_TRANSFER_VIEW,
      Permission.FINANCE_TRANSFER_APPROVE,
      // Reçus
      Permission.FINANCE_RECEIPT_GENERATE,
      // Suspensions
      Permission.FINANCE_SUSPENSION_VIEW,
      Permission.FINANCE_SUSPENSION_MANAGE,
    ],
  },
  payroll: {
    label: 'Paie Personnel',
    permissions: [
      Permission.PAYROLL_PERMANENT_VIEW,
      Permission.PAYROLL_PERMANENT_MANAGE,
      Permission.PAYROLL_PERMANENT_BANK_AUTH,
      Permission.PAYROLL_VACATAIRE_VIEW,
      Permission.PAYROLL_VACATAIRE_MANAGE,
      Permission.PAYROLL_VACATAIRE_BANK_AUTH,
    ],
  },
  financeReporting: {
    label: 'Paramètres et Reporting Financier',
    permissions: [
      // Comptes Bancaires de l'établissement
      Permission.INSTITUTION_BANK_VIEW,
      Permission.INSTITUTION_BANK_MANAGE,
      // Reporting
      Permission.FINANCE_REPORT_RECOVERY,
      Permission.FINANCE_REPORT_PAYROLL,
      Permission.FINANCE_REPORT_FORECAST,
      Permission.FINANCE_REPORT_EXPORT,
    ],
  },
  pedagogique: {
    label: 'Gestion Pédagogique',
    permissions: [
      // Gestion des Filières
      Permission.FILIERE_VIEW,
      Permission.FILIERE_MANAGE,
      // Gestion des Groupes
      Permission.GROUP_VIEW,
      Permission.GROUP_MANAGE,
      // Gestion des Attestations (student requests - viewing and validation)
      Permission.REQUEST_VIEW_ALL,
      Permission.REQUEST_VALIDATE_FINANCE,
      Permission.REQUEST_VALIDATE_ACADEMIC,
      Permission.REQUEST_MANAGE,
    ],
  },
  // Note: JURY_VIEW and JURY_MANAGE are now part of 'notesEvaluation' category
  calendar: {
    label: 'Calendrier et Planning',
    permissions: [
      Permission.CALENDAR_ANNUAL_VIEW,
      Permission.CALENDAR_ANNUAL_MANAGE,
      Permission.SCHEDULE_VIEW,
      Permission.SCHEDULE_MANAGE,
      Permission.EXAM_SCHEDULE_VIEW,
      Permission.EXAM_SCHEDULE_MANAGE,
      Permission.RATTRAPAGE_SCHEDULE_VIEW,
      Permission.RATTRAPAGE_SCHEDULE_MANAGE,
      Permission.SESSION_CANCEL,
      Permission.ROOM_OCCUPANCY_VIEW,
    ],
  },
};

// Role to Permissions mapping (default permissions for each role)
// These are used as fallback when permissions haven't been fetched from API
//
// STUDENT PERMISSION RULES:
// - DG: ALL student permissions (LOCKED - cannot be removed)
// - Branch Manager: ALL student permissions pre-checked (DG can control)
// - Directeur Pédagogique & Chef de Filière: ALL student permissions pre-checked
// - Coordinateur Académique: All EXCEPT delete, archive, suspend/activate, validate profile
// - Responsable Financier, Marketing, Commercial: Only view and create
// - Teacher: Not concerned (100% professors excluded)
export const RolePermissions: Record<UserRole, Permission[]> = {
  [UserRole.SUPER_ADMIN]: Object.values(Permission),

  // DG has ALL permissions (LOCKED - cannot be modified)
  [UserRole.DIRECTOR_GENERAL]: Object.values(Permission),

  // Branch Manager: ALL permissions pre-checked so they see everything in their
  // campus (access is scoped to their assigned campuses server-side). DG can
  // still adjust. Kept in sync with the backend getDefaultPermissionsForRole.
  [UserRole.BRANCH_MANAGER]: Object.values(Permission),

  // Directeur Pédagogique: ALL student permissions pre-checked
  [UserRole.PEDAGOGICAL_DIRECTOR]: [
    // ALL Student permissions
    Permission.STUDENT_CREATE,
    Permission.STUDENT_BULK_CREATE,
    Permission.STUDENT_VIEW,
    Permission.STUDENT_VALIDATE_MODIFICATIONS,
    Permission.STUDENT_EDIT_PROFILE,
    Permission.STUDENT_DELETE,
    Permission.STUDENT_ARCHIVE,
    Permission.STUDENT_SUSPEND_ACTIVATE,
    Permission.STUDENT_VALIDATE_PROFILE,
    Permission.STUDENT_RESET_PASSWORD,
    // Other permissions
    // Mirrors the server defaults: a DP validates staff dossiers and their modification requests
    Permission.STAFF_VALIDATE_PROFILE,
    Permission.STAFF_VALIDATE_MODIFICATIONS,
    Permission.STAFF_VIEW,
    Permission.GRADES_VIEW,
    Permission.GRADES_EDIT,
    Permission.GRADES_VALIDATE,
    // Tracking & Reports (ALL)
    Permission.TRACKING_PROFILE_VIEW,
    Permission.TRACKING_PROFILE_EXPORT,
    Permission.TRACKING_PHYSICAL_DOCS_VIEW,
    Permission.TRACKING_PHYSICAL_DOCS_EXPORT,
    Permission.EDEP_VIEW,
    Permission.EDEP_GENERATE,
    Permission.EDEP_EXPORT,
    Permission.EDEP_HISTORY_VIEW,
    // Gestion Établissement (ALL — full access by default)
    Permission.INSTITUTION_PROFILE_VIEW,
    Permission.INSTITUTION_PROFILE_MANAGE,
    Permission.INFRASTRUCTURE_VIEW,
    Permission.INFRASTRUCTURE_MANAGE,
    Permission.DEPARTMENTS_VIEW,
    Permission.DEPARTMENTS_MANAGE,
    Permission.OFFICIAL_DOCUMENTS_VIEW,
    Permission.OFFICIAL_DOCUMENTS_MANAGE,
    // Paramètres (ALL — full access by default)
    Permission.SETTINGS_USERS_VIEW,
    Permission.SETTINGS_USERS_MANAGE,
    Permission.ACADEMIC_CONFIG_VIEW,
    Permission.ACADEMIC_CONFIG_MANAGE,
    Permission.CALENDAR_SETTINGS_VIEW,
    Permission.CALENDAR_SETTINGS_MANAGE,
    // Gestion Pédagogique (ALL — full access by default)
    Permission.FILIERE_VIEW,
    Permission.FILIERE_MANAGE,
    Permission.GROUP_VIEW,
    Permission.GROUP_MANAGE,
    Permission.REQUEST_VIEW_ALL,
    Permission.REQUEST_VALIDATE_FINANCE,
    Permission.REQUEST_VALIDATE_ACADEMIC,
    Permission.REQUEST_MANAGE,
    // Calendrier et Planning (ALL — full access by default)
    Permission.CALENDAR_ANNUAL_VIEW,
    Permission.CALENDAR_ANNUAL_MANAGE,
    Permission.SCHEDULE_VIEW,
    Permission.ROOM_OCCUPANCY_VIEW,
    Permission.SCHEDULE_MANAGE,
    Permission.EXAM_SCHEDULE_VIEW,
    Permission.EXAM_SCHEDULE_MANAGE,
    Permission.RATTRAPAGE_SCHEDULE_VIEW,
    Permission.RATTRAPAGE_SCHEDULE_MANAGE,
    Permission.SESSION_CANCEL,
    // Notes & Evaluation (ALL 6 permissions)
    Permission.NOTES_VIEW,
    Permission.NOTES_MANAGE,
    Permission.ATTENDANCE_VIEW,
    Permission.ATTENDANCE_MANAGE,
    Permission.JURY_VIEW,
    Permission.JURY_MANAGE,
    // Qualité & Suivi (ALL 3 permissions)
    Permission.PROFESSOR_MONITORING_VIEW,
    Permission.EVALUATION_VIEW,
    Permission.EVALUATION_MANAGE,
    Permission.EVALUATION_EXPORT,
  ],

// Chef de Filière: ALL student permissions pre-checked
  // Tracking & Reports: ALL pre-checked
  [UserRole.PROGRAM_HEAD]: [
    // ALL Student permissions
    Permission.STUDENT_CREATE,
    Permission.STUDENT_BULK_CREATE,
    Permission.STUDENT_VIEW,
    Permission.STUDENT_VALIDATE_MODIFICATIONS,
    Permission.STUDENT_EDIT_PROFILE,
    Permission.STUDENT_DELETE,
    Permission.STUDENT_ARCHIVE,
    Permission.STUDENT_SUSPEND_ACTIVATE,
    Permission.STUDENT_VALIDATE_PROFILE,
    Permission.STUDENT_RESET_PASSWORD,
    // Other permissions
    Permission.STAFF_VIEW,
    Permission.STAFF_VALIDATE_PROFILE, // validate profiles of staff in their campus
    Permission.GRADES_VIEW,
    Permission.GRADES_EDIT,
    // Tracking & Reports (ALL)
    Permission.TRACKING_PROFILE_VIEW,
    Permission.TRACKING_PROFILE_EXPORT,
    Permission.TRACKING_PHYSICAL_DOCS_VIEW,
    Permission.TRACKING_PHYSICAL_DOCS_EXPORT,
    Permission.EDEP_VIEW,
    Permission.EDEP_GENERATE,
    Permission.EDEP_EXPORT,
    Permission.EDEP_HISTORY_VIEW,
    // Gestion Établissement (view-only + manage official documents)
    Permission.INSTITUTION_PROFILE_VIEW,
    Permission.INFRASTRUCTURE_VIEW,
    Permission.DEPARTMENTS_VIEW,
    Permission.OFFICIAL_DOCUMENTS_VIEW,
    Permission.OFFICIAL_DOCUMENTS_MANAGE,
    // Gestion Pédagogique - Filières & Groupes
    Permission.FILIERE_VIEW,
    Permission.FILIERE_MANAGE,
    Permission.GROUP_VIEW,
    Permission.GROUP_MANAGE,
    // Calendrier et Planning (ALL — full access for Chef de Filière)
    Permission.CALENDAR_ANNUAL_VIEW,
    Permission.CALENDAR_ANNUAL_MANAGE,
    Permission.SCHEDULE_VIEW,
    Permission.ROOM_OCCUPANCY_VIEW,
    Permission.SCHEDULE_MANAGE,
    Permission.EXAM_SCHEDULE_VIEW,
    Permission.EXAM_SCHEDULE_MANAGE,
    Permission.RATTRAPAGE_SCHEDULE_VIEW,
    Permission.RATTRAPAGE_SCHEDULE_MANAGE,
    Permission.SESSION_CANCEL,
    // Notes & Evaluation (ALL 6 permissions)
    Permission.NOTES_VIEW,
    Permission.NOTES_MANAGE,
    Permission.ATTENDANCE_VIEW,
    Permission.ATTENDANCE_MANAGE,
    Permission.JURY_VIEW,
    Permission.JURY_MANAGE,
    // Qualité & Suivi (ALL 3 permissions)
    Permission.PROFESSOR_MONITORING_VIEW,
    Permission.EVALUATION_VIEW,
    Permission.EVALUATION_MANAGE,
    Permission.EVALUATION_EXPORT,
  ],

  // Coordinateur Académique: All student permissions EXCEPT delete, archive, suspend/activate, validate profile
  // Tracking: Suivi des dossiers et documents physiques only, NO E-DEP
  [UserRole.ACADEMIC_COORDINATOR]: [
    // Student permissions (excluding: DELETE, ARCHIVE, SUSPEND_ACTIVATE, VALIDATE_PROFILE)
    Permission.STUDENT_CREATE,
    Permission.STUDENT_BULK_CREATE,
    Permission.STUDENT_VIEW,
    Permission.STUDENT_VALIDATE_MODIFICATIONS,
    Permission.STUDENT_EDIT_PROFILE,
    Permission.STUDENT_RESET_PASSWORD,
    // Other permissions
    Permission.STAFF_VIEW,
    Permission.GRADES_VIEW,
    // Tracking (Suivi des dossiers + documents physiques only - NO E-DEP)
    Permission.TRACKING_PROFILE_VIEW,
    Permission.TRACKING_PROFILE_EXPORT,
    Permission.TRACKING_PHYSICAL_DOCS_VIEW,
    Permission.TRACKING_PHYSICAL_DOCS_EXPORT,
    // Gestion Pédagogique - Filières (VIEW only) & Groupes (ALL)
    Permission.FILIERE_VIEW,
    // Note: NO FILIERE_MANAGE for Academic Coordinator
    Permission.GROUP_VIEW,
    Permission.GROUP_MANAGE,
    // Notes & Évaluation (VIEW only — consult grades/retakes and absences)
    Permission.NOTES_VIEW,
    Permission.ATTENDANCE_VIEW,
    // Note: NO NOTES_MANAGE / ATTENDANCE_MANAGE / JURY by default (can be granted via override)
    // Calendrier et Planning (ALL — full access for Coordinateur Académique)
    Permission.CALENDAR_ANNUAL_VIEW,
    Permission.CALENDAR_ANNUAL_MANAGE,
    Permission.SCHEDULE_VIEW,
    Permission.ROOM_OCCUPANCY_VIEW,
    Permission.SCHEDULE_MANAGE,
    Permission.EXAM_SCHEDULE_VIEW,
    Permission.EXAM_SCHEDULE_MANAGE,
    Permission.RATTRAPAGE_SCHEDULE_VIEW,
    Permission.RATTRAPAGE_SCHEDULE_MANAGE,
    Permission.SESSION_CANCEL,
    // Note: NO ACADEMIC_CONFIG_VIEW, NO ACADEMIC_CONFIG_MANAGE for Academic Coordinator
    // Qualité & Suivi (VIEW only - NO MANAGE for Academic Coordinator)
    Permission.PROFESSOR_MONITORING_VIEW,
    Permission.EVALUATION_VIEW,
    // Note: NO EVALUATION_MANAGE for Academic Coordinator by default
  ],

  // Responsable Financier: Only view and create students
  // NO Tracking & Reports permissions by default
  [UserRole.FINANCIAL_MANAGER]: [
    Permission.STUDENT_VIEW,
    Permission.STUDENT_CREATE,
    Permission.PAYMENTS_VIEW,
    Permission.PAYMENTS_MANAGE,
  ],

  // Responsable Marketing: Student management + Finance for enrollment
  [UserRole.MARKETING_MANAGER]: [
    Permission.STUDENT_VIEW,
    Permission.STUDENT_CREATE,
    // Filière view (needed for tuition grid viewing)
    Permission.FILIERE_VIEW,
    // Calendar permissions
    Permission.CALENDAR_ANNUAL_VIEW,
    Permission.SCHEDULE_VIEW,
    // Financial permissions (for enrollment/payment processing)
    Permission.FINANCE_TUITION_VIEW,
    Permission.FINANCE_PAYMENT_VIEW,
    Permission.FINANCE_PAYMENT_RECORD,
    Permission.FINANCE_RECEIPT_GENERATE,
    Permission.FINANCE_DISCOUNT_VIEW,
    Permission.FINANCE_DISCOUNT_MANAGE,
    Permission.FINANCE_TRANSFER_VIEW,
  ],

  // Responsable Commercial: Student management + Finance for enrollment
  [UserRole.COMMERCIAL_MANAGER]: [
    Permission.STUDENT_VIEW,
    Permission.STUDENT_CREATE,
    // Filière view (needed for tuition grid viewing)
    Permission.FILIERE_VIEW,
    // Calendar permissions
    Permission.CALENDAR_ANNUAL_VIEW,
    Permission.SCHEDULE_VIEW,
    // Financial permissions (for enrollment/payment processing)
    Permission.FINANCE_TUITION_VIEW,
    Permission.FINANCE_PAYMENT_VIEW,
    Permission.FINANCE_PAYMENT_RECORD,
    Permission.FINANCE_RECEIPT_GENERATE,
    Permission.FINANCE_DISCOUNT_VIEW,
    Permission.FINANCE_DISCOUNT_MANAGE,
    Permission.FINANCE_TRANSFER_VIEW,
  ],

  // Call Center Agent: No permissions (marketing module removed)
  [UserRole.CALL_CENTER_AGENT]: [],

  // Teacher: 100% professors NOT concerned with student management
  [UserRole.TEACHER]: [
    // No student management permissions.
    // Mirrors the backend defaults (permissions.service.ts, TEACHER). This
    // list is what the sidebar uses when the permissions request fails — a
    // shorter list here silently hid the Calendrier menu from every
    // professor who logged in during an API restart.
    Permission.GRADES_VIEW,
    Permission.GRADES_EDIT,
    Permission.NOTES_VIEW,
    Permission.NOTES_MANAGE,
    Permission.ATTENDANCE_VIEW,
    Permission.ATTENDANCE_MANAGE,
    Permission.CALENDAR_ANNUAL_VIEW,
    Permission.SCHEDULE_VIEW,
  ],

  [UserRole.STUDENT]: [],

  [UserRole.PARENT]: [],
};

// User status
export type UserStatus = 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'PENDING' | 'WITHDRAWN';

// User interface
export interface User {
  id: string;
  email: string | null;
  username?: string | null;
  firstName: string;
  lastName: string;
  role: UserRole;
  status?: UserStatus;
  tenantId: string | null;
  tenantName?: string;
  tenantLogoUrl?: string;
  avatarUrl?: string;
  phone?: string;
  isActive: boolean;
  lastLoginAt?: string;
  mustChangePassword?: boolean;
  profileSubmissionStatus?: 'DRAFT' | 'PENDING_VALIDATION' | 'APPROVED' | 'REJECTED';
  profileCompleted?: boolean;
  isAlsoProfessor?: boolean;
  // Job function from profile
  fonction?: string;
  // Profile deadline tracking
  isProfileDeadlineBlocked?: boolean;
  profileDeadlineDate?: string | null;
  profileDaysRemaining?: number | null;
  // Student-specific
  academicYearId?: string | null;
  // Enabled solutions for the tenant (e.g., ['academic', 'finance', 'marketing'])
  enabledSolutions?: string[];
  // Tenant status (for checking suspension)
  tenantStatus?: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'TRIAL';
  // Tenant subdomain and login URL for white-label multi-tenancy
  tenantSubdomain?: string;
  tenantLoginUrl?: string;
}

// Tenant interface
export interface Tenant {
  id: string;
  name: string;
  slug: string;
  subdomain: string;
  logoUrl?: string;
  primaryColor?: string;
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'TRIAL';
}

// API Response types
export interface ApiResponse<T> {
  data: T;
  message?: string;
}

export interface ApiError {
  statusCode: number;
  message: string;
  errors?: Array<{ field: string; message: string }>;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Navigation item interface
export interface NavItem {
  label: string;
  path: string;
  icon: string;
  permission?: Permission;
  children?: NavItem[];
}
